// ORCA360 - Compact Processing Status Component
// Compact version for bottom of page display

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface CompactProcessingStatusProps {
  sessionId: string;
  onComplete?: () => void;
}

interface ProcessingJob {
  id: string;
  job_type: string;
  status: string;
  progress: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export default function CompactProcessingStatus({ sessionId, onComplete }: CompactProcessingStatusProps) {
  const [jobs, setJobs] = useState<ProcessingJob[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadJobs();

    // Set up real-time subscription for job updates
    const channel = supabase
      .channel(`processing-compact-${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'processing_jobs',
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          console.log('[CompactProcessingStatus] Job update:', payload);
          loadJobs();

          // Check if any job completed
          if (payload.new && (payload.new as any).status === 'completed') {
            onComplete?.();
          }
        }
      )
      .subscribe();

    // Add polling as backup (every 10 seconds for compact view)
    const pollInterval = setInterval(() => {
      // Only poll if there are processing jobs
      if (jobs.some(j => j.status === 'processing' || j.status === 'queued' || j.status === 'pending')) {
        loadJobs();
      }
    }, 10000); // Poll every 10 seconds

    // Cleanup
    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [sessionId]);

  const loadJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('processing_jobs')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(3); // Only show latest 3 jobs in compact view

      if (error) throw error;

      setJobs(data || []);
    } catch (error) {
      console.error('[CompactProcessingStatus] Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getJobIcon = (jobType: string): string => {
    const icons: Record<string, string> = {
      transcription: '🎤',
      diarization: '👥',
      event_detection: '🔍',
      assessment_generation: '📊',
    };
    return icons[jobType] ?? '⚙️';
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      queued: 'text-slate-600',
      processing: 'text-yellow-600',
      completed: 'text-green-600',
      failed: 'text-red-600',
      cancelled: 'text-gray-600',
    };
    return colors[status] ?? 'text-slate-600';
  };

  const getJobLabel = (jobType: string): string => {
    const labels: Record<string, string> = {
      transcription: 'Transcription',
      diarization: 'Speaker ID',
      event_detection: 'Event Detection',
      assessment_generation: 'AI Assessment',
    };
    return labels[jobType] ?? jobType.replace('_', ' ');
  };

  if (loading) {
    return (
      <div className="text-center py-2">
        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
        <span className="ml-2 text-xs text-gray-500">Loading...</span>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="text-center py-2">
        <span className="text-xs text-gray-500">No processing jobs</span>
      </div>
    );
  }

  // Check if any jobs are currently processing
  const isProcessing = jobs.some((j) => j.status === 'processing' || j.status === 'queued');

  return (
    <div className="space-y-2">
      {/* Summary */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-600">
          {jobs.filter((j) => j.status === 'completed').length} of {jobs.length} complete
        </span>
        {isProcessing && (
          <div className="flex items-center space-x-1">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-500"></div>
            <span className="text-xs text-yellow-600">Processing...</span>
          </div>
        )}
      </div>

      {/* Job List */}
      <div className="space-y-1">
        {jobs.map((job) => (
          <div key={job.id} className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-2">
              <span className="text-sm">{getJobIcon(job.job_type)}</span>
              <span className="text-gray-700">{getJobLabel(job.job_type)}</span>
            </div>
            <div className="flex items-center space-x-2">
              {job.status === 'processing' && (
                <span className="text-gray-500">{job.progress}%</span>
              )}
              <span className={`font-medium ${getStatusColor(job.status)}`}>
                {job.status === 'completed' && '✓'}
                {job.status === 'processing' && '⏳'}
                {job.status === 'failed' && '❌'}
                {job.status === 'queued' && '⏸️'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Error Summary */}
      {jobs.some(j => j.status === 'failed') && (
        <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
          ⚠️ Some jobs failed. Check full status above for details.
        </div>
      )}
    </div>
  );
}

